/**
 * Composable transaction (international & domestic order).
 *
 * staleTime list: 30 detik (data operasional, harus relatif fresh)
 * staleTime detail: 5 menit
 *
 * Params bisa ref/computed/plain object. TanStack akan otomatis re-fetch saat key berubah.
 *
 * Pemakaian (di setup):
 *   const { data, isLoading, refetch } = useInternationalOrders({ page: 1, limit: 10 });
 *
 * Untuk filter dinamis dengan reactivity:
 *   const params = reactive({ page: 1, limit: 10, search: "" });
 *   const { data } = useInternationalOrders(computed(() => params));
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchInternationalOrders,
  fetchInternationalOrdersWithFilter,
  fetchInternationalOrdersFilterAllAgen,
  fetchInternationalOrdersFilterSuratJalan,
  fetchInternationalOrderById,
  fetchInternationalAgenGeneral,
  fetchTotalOrderInternational,
  fetchTotalShipmentFee,
  fetchPelanggan,
  fetchInternationalOrderLast,
  fetchInternationalOrderByField,
  fetchInternationalOrderSearchByField,
  fetchInternationalAdvanceSearch,
  createInternationalOrder,
  updateInternationalOrder,
  deleteInternationalOrder,
  updatePositionOrderInternational,
  resetManifestInternational,
  scanBarcodeInternational,
  importInternationalOrder,
  fetchDomesticOrders,
  fetchDomesticOrderById,
  fetchDomesticOrderLast,
  fetchDomesticOrderByField,
  fetchDomesticOrderSearchByField,
  createDomesticOrder,
  updateDomesticOrder,
  deleteDomesticOrder,
  updatePositionOrderDomestic,
} from "@/queries/api/transaction";

const LIST_STALE = 30 * 1000; // 30 detik
const DETAIL_STALE = 5 * 60 * 1000; // 5 menit

function p(input) {
  /* Unwrap ref/computed jika perlu */
  return unref(input);
}

/* ============================================================
 * International Orders — Queries
 * ============================================================ */

export function useInternationalOrders(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.international.list(p(params))),
    queryFn: () => fetchInternationalOrders(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInternationalOrdersWithFilter(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.international.listFilter(p(params))),
    queryFn: () => fetchInternationalOrdersWithFilter(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInternationalOrdersFilterAllAgen(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "filter-all-agen",
      p(params),
    ]),
    queryFn: () => fetchInternationalOrdersFilterAllAgen(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInternationalOrdersFilterSuratJalan(
  params = {},
  options = {}
) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "filter-surat-jalan",
      p(params),
    ]),
    queryFn: () => fetchInternationalOrdersFilterSuratJalan(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInternationalOrder(id, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.international.detail(p(id))),
    queryFn: () => fetchInternationalOrderById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

export function useInternationalAgenGeneral(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "agen-general",
      p(params),
    ]),
    queryFn: () => fetchInternationalAgenGeneral(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useTotalOrderInternational(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.international.statistic(p(params))),
    queryFn: () => fetchTotalOrderInternational(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useTotalShipmentFee(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "total-shipment-fee",
      p(params),
    ]),
    queryFn: () => fetchTotalShipmentFee(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function usePelanggan(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.international.all, "pelanggan", p(params)]),
    queryFn: () => fetchPelanggan(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInternationalOrderLast(options = {}) {
  return useQuery({
    queryKey: [...keys.international.all, "last"],
    queryFn: fetchInternationalOrderLast,
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useInternationalOrderByField(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "by-field",
      p(params),
    ]),
    queryFn: () => fetchInternationalOrderByField(p(params)),
    staleTime: LIST_STALE,
    enabled: computed(() => {
      const v = p(params);
      return !!(v && Object.keys(v).length > 0);
    }),
    ...options,
  });
}

export function useInternationalOrderSearchByField(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "search-by-field",
      p(params),
    ]),
    queryFn: () => fetchInternationalOrderSearchByField(p(params)),
    staleTime: LIST_STALE,
    enabled: computed(() => {
      const v = p(params);
      return !!(v && Object.keys(v).length > 0);
    }),
    ...options,
  });
}

export function useInternationalAdvanceSearch(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.international.all,
      "advance-search",
      p(params),
    ]),
    queryFn: () => fetchInternationalAdvanceSearch(p(params)),
    staleTime: LIST_STALE,
    enabled: computed(() => {
      const v = p(params);
      return !!(v && Object.keys(v).length > 0);
    }),
    ...options,
  });
}

/* ============================================================
 * International Orders — Mutations
 * ============================================================ */

function invalidateInternational(qc) {
  return qc.invalidateQueries({ queryKey: keys.international.all });
}

export function useCreateInternationalOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInternationalOrder,
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

export function useUpdateInternationalOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateInternationalOrder,
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

export function useDeleteInternationalOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteInternationalOrder,
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

export function useUpdatePositionOrderInternational(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePositionOrderInternational,
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

export function useResetManifestInternational(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resetManifestInternational,
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

export function useScanBarcodeInternational(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: scanBarcodeInternational,
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

export function useImportInternationalOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, config }) => importInternationalOrder(payload, config),
    onSuccess: () => invalidateInternational(qc),
    ...options,
  });
}

/* ============================================================
 * Domestic Orders — Queries
 * ============================================================ */

export function useDomesticOrders(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.domestic.list(p(params))),
    queryFn: () => fetchDomesticOrders(p(params)),
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useDomesticOrder(id, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.domestic.detail(p(id))),
    queryFn: () => fetchDomesticOrderById(p(id)),
    staleTime: DETAIL_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

export function useDomesticOrderLast(options = {}) {
  return useQuery({
    queryKey: [...keys.domestic.all, "last"],
    queryFn: fetchDomesticOrderLast,
    staleTime: LIST_STALE,
    ...options,
  });
}

export function useDomesticOrderByField(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.domestic.all, "by-field", p(params)]),
    queryFn: () => fetchDomesticOrderByField(p(params)),
    staleTime: LIST_STALE,
    enabled: computed(() => {
      const v = p(params);
      return !!(v && Object.keys(v).length > 0);
    }),
    ...options,
  });
}

export function useDomesticOrderSearchByField(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => [
      ...keys.domestic.all,
      "search-by-field",
      p(params),
    ]),
    queryFn: () => fetchDomesticOrderSearchByField(p(params)),
    staleTime: LIST_STALE,
    enabled: computed(() => {
      const v = p(params);
      return !!(v && Object.keys(v).length > 0);
    }),
    ...options,
  });
}

/* ============================================================
 * Domestic Orders — Mutations
 * ============================================================ */

function invalidateDomestic(qc) {
  return qc.invalidateQueries({ queryKey: keys.domestic.all });
}

export function useCreateDomesticOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDomesticOrder,
    onSuccess: () => invalidateDomestic(qc),
    ...options,
  });
}

export function useUpdateDomesticOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateDomesticOrder,
    onSuccess: () => invalidateDomestic(qc),
    ...options,
  });
}

export function useDeleteDomesticOrder(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDomesticOrder,
    onSuccess: () => invalidateDomestic(qc),
    ...options,
  });
}

export function useUpdatePositionOrderDomestic(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePositionOrderDomestic,
    onSuccess: () => invalidateDomestic(qc),
    ...options,
  });
}
